import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/navbar/ui/Navbar';
import Footer from '../../../components/footer/ui/Footer';
import WhatsAppButton from '../../../components/whatsapp-button/ui/WhatsAppButton';
import ScrollToTop from '../../../components/scroll-to-top/ui/ScrollToTop';
import SEO from '../../../components/seo/SEO';
import FormDialog from '../../../components/form-dialog/ui/FormDialog';
import { useLanguage } from '../../../../context/LanguageContext';
import { Credentials } from '../../../../assets/features/Credentials';
import { submitWecallForm } from '../../../components/email/service/appointmentFormHandler';
import wecallBgImage from '../../../../assets/logo/wecall/wecall2.webp';
import wecallImage from '../../../../assets/logo/wecall/wecall.webp';
import { seoData } from '../../../../data/seoData';
import '../style/wecall.css';

const WeCallPage: React.FC = () => {
    const { language } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        phone: '',
        email: '',
        serviceSelection: '',
        wantedDay: '',
        wantedSlot: '',
        notes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dialogState, setDialogState] = useState<{
        isOpen: boolean;
        type: 'success' | 'error';
        message?: string;
    }>({
        isOpen: false,
        type: 'success'
    });
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
    const [_recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);

    useEffect(() => {
        // Scroll to top on mount
        window.scrollTo(0, 0);
        
        // Set minimum date for date input
        const dateInput = document.getElementById('wantedDay') as HTMLInputElement;
        if (dateInput) {
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            dateInput.min = `${yyyy}-${mm}-${dd}`;
        }

        // Debug: Check if reCAPTCHA script is loaded
        
        const checkRecaptcha = () => {
            if (typeof window.grecaptcha !== 'undefined') {
                setRecaptchaLoaded(true);
                
                window.grecaptcha.ready(() => {
                    
                    // Manually render the reCAPTCHA widget
                    setTimeout(() => {
                        const recaptchaContainer = document.querySelector('.g-recaptcha');
                        if (recaptchaContainer && !recaptchaContainer.querySelector('iframe')) {
                            try {
                                const widgetId = window.grecaptcha.render(recaptchaContainer as HTMLElement, {
                                    'sitekey': Credentials.RECAPTCHA_SITE_KEY,
                                    'theme': 'light',
                                    'size': 'normal'
                                });
                                setRecaptchaWidgetId(widgetId);
                                
                                // Verify rendering
                                setTimeout(() => {
                                    const iframe = recaptchaContainer.querySelector('iframe');
                                    if (iframe) {
                                    } else {
                                    }
                                }, 500);
                            } catch (_) {
                                setRecaptchaError('Failed to render reCAPTCHA widget');
                            }
                        } else if (recaptchaContainer && recaptchaContainer.querySelector('iframe')) {
                        } else {
                        }
                    }, 500);
                });
            } else {
            }
        };

        checkRecaptcha();

        let attempts = 0;
        const maxAttempts = 20;
        const checkInterval = setInterval(() => {
            attempts++;
            if (typeof window.grecaptcha !== 'undefined') {
                setRecaptchaLoaded(true);
                setRecaptchaError(null);
                clearInterval(checkInterval);
            } else if (attempts >= maxAttempts) {
                // reCAPTCHA is consent-gated: while the gate notice is shown
                // the script is intentionally absent — keep polling instead of
                // reporting a loading failure.
                if (!document.querySelector('.recaptcha-consent-note')) {
                    setRecaptchaError('reCAPTCHA failed to load. Please refresh the page.');
                    clearInterval(checkInterval);
                }
            }
        }, 500);

        return () => clearInterval(checkInterval);
    }, []);

    // Additional check after component fully renders
    useEffect(() => {
        const checkWidget = () => {
            const recaptchaContainer = document.querySelector('.g-recaptcha');
            if (recaptchaContainer) {
                const iframe = recaptchaContainer.querySelector('iframe');
                if (!iframe) {
                } else {
                }
            } else {
            }
        };

        // Check immediately
        checkWidget();
        
        // Check after a delay to catch late renders
        const timeout = setTimeout(checkWidget, 2000);
        
        return () => clearTimeout(timeout);
    }, [recaptchaLoaded]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        
        // Validate form
        if (!formData.name || !formData.surname || !formData.phone) {
            const message = language === 'pt' 
                ? 'Por favor, preencha todos os campos obrigatórios.'
                : 'Please fill in all required fields.';
            setDialogState({
                isOpen: true,
                type: 'error',
                message
            });
            return;
        }
        
        // Validate reCAPTCHA - REQUIRED
        let recaptchaResponse = '';
        if (typeof window.grecaptcha !== 'undefined') {
            try {
                recaptchaResponse = window.grecaptcha.getResponse();
            } catch (e) {
            }
        }
        
        if (!recaptchaResponse) {
            const recaptchaTextarea = document.querySelector('#g-recaptcha-response') as HTMLTextAreaElement;
            if (recaptchaTextarea) {
                recaptchaResponse = recaptchaTextarea.value || '';
            }
        }
        
        if (!recaptchaResponse || recaptchaResponse === '') {
            const message = language === 'pt' 
                ? 'Por favor, complete o reCAPTCHA.'
                : 'Please complete the reCAPTCHA.';
            setDialogState({
                isOpen: true,
                type: 'error',
                message
            });
            return;
        }
        
        setIsSubmitting(true);

        const submissionPayload = {
                name: formData.name,
                surname: formData.surname,
                phone: formData.phone,
                email: formData.email || undefined,
                serviceSelection: formData.serviceSelection || undefined,
                wantedDay: formData.wantedDay || undefined,
                wantedSlot: formData.wantedSlot || undefined,
                notes: formData.notes || undefined,
                form_location: 'pagina_ligamos' as const
            };

        // Optimistic UI: show success immediately, reset form, fire-and-forget the send
            setDialogState({
                isOpen: true,
                type: 'success'
            });
            // Reset form
            setFormData({
                name: '',
                surname: '',
                phone: '',
                email: '',
                serviceSelection: '',
                wantedDay: '',
                wantedSlot: '',
                notes: ''
            });
        if (typeof window.grecaptcha !== 'undefined') {
            try { window.grecaptcha.reset(); } catch (e) { /* noop */ }
        }
        setIsSubmitting(false);

        submitWecallForm(submissionPayload, language, recaptchaResponse).catch((err) => {
            console.error('Background mail send failed:', err);
        });
    };

    return (
        <>
            <SEO 
                title={seoData.wecall.title}
                description={seoData.wecall.description}
                keywords={seoData.wecall.keywords}
                canonical={seoData.wecall.canonical}
            />
        <div className="wecall-page">
            <Navbar />
            <section className="page-header">
                <div
                    className="page-header__bg"
                    style={{
                        backgroundImage: `url(${wecallBgImage})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        height: '380px'
                    }}
                ></div>
                <div className="container">
                    <div className="page-header__inner">
                        <div className="page-header__img-1">
                            <img loading="lazy" 
                                src={wecallImage} 
                                alt="Roady Leiria" 
                                style={{ width: '250px', height: '300px', objectFit: 'contain' }}
                            />
                        </div>
                        <h3>{language === 'pt' ? 'Nós ligamos' : "We'll Call"}</h3>
                        <div className="thm-breadcrumb__inner">
                            <ul className="thm-breadcrumb list-unstyled">
                                <li><Link to="/">{language === 'pt' ? 'Início' : 'Home'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li>{language === 'pt' ? 'Nós ligamos' : "We'll Call"}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            
            <section className="contact-page">
                <div className="container">
                    <div className="contact-page__bottom">
                        <div className="contact-page__form-box">
                            <h3 className="comment-one__title">
                                {language === 'pt' ? 'Pedir Ligação' : 'Request a Call'}
                            </h3>
                            <p className="comment-one__text">
                                {language === 'pt' 
                                    ? 'Preencha o formulário abaixo e ligamos no horário preferido.'
                                    : 'Fill out the form below and we will call you at your preferred time.'}
                            </p>
                            <form className="contact-page__form" id="wecall-form" onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="name">{language === 'pt' ? 'Nome *' : 'Name *'}</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                placeholder={language === 'pt' ? 'Insira o seu nome' : 'Enter your name'}
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-user"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="surname">{language === 'pt' ? 'Apelido *' : 'Surname *'}</label>
                                            <input
                                                type="text"
                                                id="surname"
                                                name="surname"
                                                placeholder={language === 'pt' ? 'Insira o seu apelido' : 'Enter your surname'}
                                                value={formData.surname}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-user"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="phone">{language === 'pt' ? 'Número de Telefone *' : 'Phone Number *'}</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                placeholder={language === 'pt' ? 'Insira o seu número de telefone' : 'Enter your phone number'}
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-phone-call"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="email">{language === 'pt' ? 'Endereço de Email' : 'Email Address'}</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder={language === 'pt' ? 'Insira o seu email' : 'Enter your email address'}
                                                value={formData.email}
                                                onChange={handleInputChange}
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-email"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="serviceSelection">{language === 'pt' ? 'Seleção de Serviço' : 'Service Selection'}</label>
                                            <div className="form-page__service-selector">
                                                <select
                                                    id="serviceSelection"
                                                    name="serviceSelection"
                                                    value={formData.serviceSelection}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">{language === 'pt' ? 'Escolha um serviço...' : 'Choose a service...'}</option>
                                                    <option value="workshop-services-pack">
                                                        {language === 'pt' ? 'Pacote de Serviços de Oficina' : 'Workshop Services Pack'}
                                                    </option>
                                                    <option value="air-conditioning">
                                                        {language === 'pt' ? 'Ar Condicionado' : 'Air Conditioning'}
                                                    </option>
                                                    <option value="washing">
                                                        {language === 'pt' ? 'Lavagem' : 'Washing'}
                                                    </option>
                                                    <option value="headlight-restoration">
                                                        {language === 'pt' ? 'Restauro de Faróis' : 'Headlight Restoration'}
                                                    </option>
                                                    <option value="battery-service">
                                                        {language === 'pt' ? 'Serviço de Bateria' : 'Battery Service'}
                                                    </option>
                                                    <option value="brake-service">
                                                        {language === 'pt' ? 'Serviço de Travões' : 'Brake Service'}
                                                    </option>
                                                    <option value="oil-change">
                                                        {language === 'pt' ? 'Mudança de Óleo & Filtros' : 'Oil Change & Filters'}
                                                    </option>
                                                    <option value="engine-repair">
                                                        {language === 'pt' ? 'Reparação de Motor' : 'Engine Repair'}
                                                    </option>
                                                    <option value="tire-services">
                                                        {language === 'pt' ? 'Serviços de Pneus & Rodas' : 'Tire & Wheel Services'}
                                                    </option>
                                                    <option value="diagnostic-test">
                                                        {language === 'pt' ? 'Teste Diagnóstico' : 'Diagnostic Test'}
                                                    </option>
                                                    <option value="inspection">
                                                        {language === 'pt' ? 'Inspeção' : 'Inspection'}
                                                    </option>
                                                    <option value="general-mechanics">
                                                        {language === 'pt' ? 'Mecânica Geral' : 'General Mechanics'}
                                                    </option>
                                                    <option value="alignment">
                                                        {language === 'pt' ? 'Alinhamento' : 'Alignment'}
                                                    </option>
                                                    <option value="shock-absorbers">
                                                        {language === 'pt' ? 'Amortecedores' : 'Shock Absorbers'}
                                                    </option>
                                                    <option value="other">
                                                        {language === 'pt' ? 'Outro Serviço' : 'Other Service'}
                                                    </option>
                                                </select>
                                                <span className="form-page__input-icon">
                                                    <i className="icon-wrench"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="wantedDay">{language === 'pt' ? 'Dia Preferido' : 'Preferred Day'}</label>
                                            <input
                                                type="date"
                                                id="wantedDay"
                                                name="wantedDay"
                                                value={formData.wantedDay}
                                                onChange={handleInputChange}
                                                placeholder={language === 'pt' ? 'Selecionar dia' : 'Select day'}
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-calendar"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="wantedSlot">{language === 'pt' ? 'Período Preferido' : 'Preferred Time Slot'}</label>
                                            <div className="form-page__service-selector">
                                                <select
                                                    id="wantedSlot"
                                                    name="wantedSlot"
                                                    value={formData.wantedSlot}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">{language === 'pt' ? 'Escolher período...' : 'Choose a time slot...'}</option>
                                                    <option value="09:00-12:00">09:00 - 12:00</option>
                                                    <option value="14:00-19:00">14:00 - 19:00</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-12 col-lg-12">
                                        <div className="form-page__input-box">
                                            <label htmlFor="notes">{language === 'pt' ? 'Notas' : 'Notes'}</label>
                                            <textarea
                                                id="notes"
                                                name="notes"
                                                placeholder={language === 'pt' ? 'Notas adicionais (opcional)' : 'Additional notes (optional)'}
                                                value={formData.notes}
                                                onChange={handleInputChange}
                                            ></textarea>
                                            <span className="form-page__input-icon">
                                                <i className="icon-comment"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-12 col-lg-12">
                                        <div className="appointment-one__checkbox-box">
                                            <div className="appointment-one__checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="privacyPolicyWecall"
                                                    name="privacy_policy"
                                                    required
                                                />
                                                <label htmlFor="privacyPolicyWecall">
                                                    <span className="appointment-one__checkbox-mark"></span>
                                                    {language === 'pt'
                                                        ? <>Aceito a <a href="/politica-privacidade" className="privacy-policy-link-red" target="_blank" rel="noopener noreferrer">Política de Privacidade</a> e concordo com o tratamento dos meus dados pessoais *</>
                                                        : <>I accept the <a href="/politica-privacidade" className="privacy-policy-link-red" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and agree to the processing of my personal data *</>}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    {/* reCAPTCHA and Submit Button */}
                                    <div className="col-xl-12 col-lg-12">
                                        <div className="appointment-one__recaptcha-btn-row">
                                            <div className="appointment-one__recaptcha-box">
                                                <div className="g-recaptcha" data-sitekey={Credentials.RECAPTCHA_SITE_KEY}></div>
                                                {/* Debug info */}
                                                {!recaptchaLoaded && !recaptchaError && (
                                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                        Loading reCAPTCHA...
                                                    </div>
                                                )}
                                                {recaptchaError && (
                                                    <div style={{ fontSize: '12px', color: '#d90202', marginTop: '5px' }}>
                                                        ⚠️ {recaptchaError}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="appointment-one__btn-box">
                                            <button 
                                                type="submit" 
                                                    className="thm-btn budget-page__btn"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting 
                                                    ? (language === 'pt' ? 'Por favor aguarde...' : 'Please wait...')
                                                    : (language === 'pt' ? 'Enviar Pedido' : 'Submit Request')
                                                }
                                                <span><i className="icon-next"></i></span>
                                            </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
            
            <FormDialog
                isOpen={dialogState.isOpen}
                type={dialogState.type}
                message={dialogState.message}
                onClose={() => setDialogState({ ...dialogState, isOpen: false })}
            />
            
            <Footer />
            <WhatsAppButton />
            <ScrollToTop />
        </div>
        </>
    );
};

export default WeCallPage;
